<template>
  <v-form @submit.prevent="submitForm" ref="formRef" v-model="formValid">
    <v-card class="px-5 py-3">
      <v-card-title class="d-flex justify-space-between align-center mb-3">
        <span>{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <div v-if="!fields?.length">
          <v-alert type="warning" border="start" prominent>
            No hay campos definidos para este formulario.
          </v-alert>
        </div>

        <v-row v-else>
          <template v-for="field in fields" :key="field.value">
            <v-col :cols="12" :md="field.col || 12" :sm="field.mdcol || 12">
              <!-- Normal field -->
              <component
                v-if="!field.isJsonArray"
                :is="field.component"
                v-model="form[field.value]"
                v-bind="getFieldProps(field)"
                :rules="field.required ? [v => !!v || 'Campo obligatorio'] : []"
                variant="solo-filled"
              />
              <!-- Json array field -->
              <component
                v-else
                :is="field.component"
                v-model="form[field.value]"
                :fields="field.children"
                :label="field.label"
                :summary="field.summary"
                :multiple="multiple"
                variant="solo-filled"
              />
            </v-col>
          </template>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="cancelForm">Cancelar</v-btn>
        <v-btn type="submit" color="primary" :disabled="!formValid">Confirmar</v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue';
import type { FormField } from '@/types/form';

// 🔹 NUEVO: mismos helpers que usas en CreateForm
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import debounce from 'lodash.debounce';

const props = defineProps<{
  fields: FormField[];
  formTitle?: string;
  initialValues?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void;
  onCancel?: () => void;
  multiple?: boolean;
}>();

const form = ref<Record<string, any>>({});
const formRef = ref();
const formValid = ref(true);

// 🔹 NUEVO: estados para loaders por campo y debounced fetchers
const fieldLoading = ref<Record<string, boolean>>({});
const debouncedFetchers: Record<string, (val: string) => void> = {};

// 🔹 NUEVO: arma filtros para fetchOptions
const getFetchParams = (field: FormField, currentForm: any, val: string = '') => {
  let params: Record<string, any> = {};
  if (field.searchValue && val !== '') {
    params[`filter[${field.searchValue}]`] = val;
  }
  if (typeof field.fetchParams === 'function') {
    params = { ...params, ...field.fetchParams(currentForm) };
  }
  return params;
};

// Inicializa el form (soporta initialValues)
const resetForm = () => {
  fieldLoading.value = {};
  props.fields.forEach((f) => {
    if (f.isJsonArray) {
      form.value[f.value] = props.initialValues?.[f.value] || [{}];
    } else {
      form.value[f.value] = props.initialValues?.[f.value] ?? (f.multiple ? [] : '');
    }
  });
};
resetForm();

// Limpia el formulario si cambian los campos o initialValues
watch(
  () => [props.fields, props.initialValues],
  resetForm,
  { immediate: false }
);

// Limpia el form al desmontar
onBeforeUnmount(() => {
  Object.keys(form.value).forEach((k) => (form.value[k] = ''));
});

const getFieldProps = (field: FormField) => {
  const p: Record<string, any> = {
    label: field.label,
    placeholder: field.placeholder ?? '',
    clearable: true,
    density: 'compact',
    type: field.type || 'text',
    prefix: field.prefix,
    suffix: field.suffix,
    'prepend-inner-icon': field.icon,
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    multiple: field.multiple || false,
    range: field.range,
    module: field.module,
    loading: !!fieldLoading.value[field.value],
    // títulos/valores por defecto
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
    'hide-no-data': true,
    ...field.props, // Permite pasar props personalizados por campo
  };

  // Asegura que items sea reactivo
  p.items = field.options || [];

  // 🔹 NUEVO: Soporte de fetchOptions (Autocomplete/Select remoto)
  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    const fetchFn = remoteOptionsRegistry[field.fetchOptions];

    // Crea el debounced una sola vez
    if (!debouncedFetchers[field.value]) {
      debouncedFetchers[field.value] = debounce(async (val: string) => {
        fieldLoading.value[field.value] = true;
        try {
          const params = getFetchParams(field, form.value, val);
          const results = await fetchFn(params);
          field.options = Array.isArray(results) ? [...results] : [];
        } finally {
          fieldLoading.value[field.value] = false;
        }
      }, 500);
    }

    // 1) Fetch al enfocar (abre dropdown sin search para pre-cargar)
    p.onFocus = (open: boolean) => {
      if (open && (!field.options || field.options.length === 0)) {
        fieldLoading.value[field.value] = true;
        fetchFn(getFetchParams(field, form.value, ''))
          .then((results: any[]) => {
            field.options = Array.isArray(results) ? [...results] : [];
          })
          .finally(() => (fieldLoading.value[field.value] = false));
      }
    };

    // 2) Búsqueda en vivo (Vuetify 3: v-model:search -> update:search)
    p['onUpdate:search'] = (val: string) => {
      debouncedFetchers[field.value](val || '');
    };
  }

  return p;
};

const submitForm = () => {
  if (!formRef.value?.validate()) return;
  props.onSubmit({ ...form.value });
  resetForm();
};

const cancelForm = () => {
  resetForm();
  props.onCancel?.();
};
</script>
